import { useState, useRef } from "react";
import * as Sentry from "@sentry/react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import logo from "../assets/logo.svg";

export default function ConnectDialog({ open, setOpen, onSuccess }) {
  const inputRef = useRef(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");

  const connectDevice = async () => {
    try {
      const apiBaseURL = import.meta.env.VITE_API_BASE_URL;
      const response = await fetch(apiBaseURL + "connect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(code),
      });
      const connectReponse = await response.text();
      if (response.status !== 200) {
        setError(connectReponse);
        return
      }

      onSuccess();
      setOpen(false);

    } catch (error) {
      Sentry.captureException(error);
      setError("Error connecting device.");
      console.error("Error connecting device:", error);
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setCode(value);

    // Validate the length of the code
    if (value.length === 6) {
      setError("");
    } else {
      setError("The code must be 6 characters.");
    }
  };

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div>
              <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-cyan-200">
                <img alt="Fungistics" src={logo} className="" />
              </div>
              <div className="mt-3 text-center sm:mt-5">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold text-gray-900"
                >
                  Connect your device
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    {`Enter your device "connect code" below.`}
                    <br /> You can find it on the bottom of your device.
                  </p>
                </div>
                <form onSubmit={connectDevice}>
                  <div className="mt-2 grid grid-cols-1">
                    <input
                      id="code"
                      ref={inputRef}
                      name="code"
                      type="text"
                      value={code}
                      onChange={handleChange}
                      maxLength={6}
                      placeholder=""
                      aria-invalid={error ? "true" : "false"}
                      aria-describedby={error ? "code-error" : undefined}
                      className={`col-start-1 row-start-1 block rounded-md bg-white py-1.5 pl-3 pr-10 outline outline-1 -outline-offset-1 text-xl focus:outline focus:outline-2 focus:-outline-offset-2 sm:pr-9 ${error
                        ? 'outline-red-300 placeholder:text-red-300 focus:outline-red-600'
                        : 'outline-gray-300 placeholder:text-gray-400 focus:outline-cyan-800'
                        }`}
                    />
                    {error && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                        className="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4"
                      >
                        <path
                          fillRule="evenodd"
                          d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {error && (
                      <p id="code-error" className="mt-2 text-sm text-red-600">
                        {error}
                      </p>
                    )}
                  </div>
                  <div className="mt-5 sm:mt-6 max-w-6xl">
                    <button
                      type="button"
                      onClick={connectDevice}
                      disabled={!!error || code.length < 1}
                      className={`px-6 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-800  ${error || code.length < 1
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-cyan-800 hover:bg-cyan-700"
                        }`}
                    >
                      Connect
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
